import {
  DashboardGroupedTable,
  type TGroupedTableColumn,
} from 'App/dashboards/_components/DashboardGroupedTable'
import { formatApplicationRate } from 'App/dashboards/_utils/applicationRate'
import { getCropColumn } from 'App/dashboards/_utils/getCropColumn'
import {
  useIrrigationZoneActivity,
  type TIrrigationZoneRow,
} from 'App/dashboards/_utils/useIrrigationZoneActivity'
import { withCropNames, type TZoneCrops } from 'App/dashboards/_utils/zoneCrops'
import { translate } from 'i18n/i18n'
import type moment from 'moment-timezone'
import { useEffect, useMemo } from 'react'
import { checkAuthorization } from 'utils/checkAuthorization'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'
import { minutesToHoursAndMinutes } from 'utils/minutesToHoursAndMinutes'
import { unitConverter } from 'utils/unitConverter/unitConverter'

// Empty string for the CSV/collapse value (CSV blank = missing); the table renders a dash instead.
const formatVolume = (mm: number | null): string =>
  mm === null ? '' : unitConverter.irrigationActivity(mm).valueWithSuffix()

const formatDuration = (ms: number | null): string =>
  ms === null ? '' : minutesToHoursAndMinutes(ms / 60000)

const formatRain = (sum: number | null): string =>
  sum === null ? '' : unitConverter.precipitation(sum).valueWithSuffix()

const formatEtc = (sum: number | null): string =>
  sum === null ? '' : unitConverter.evapotranspiration(sum).valueWithSuffix()

type TZoneRowWithCrop = TIrrigationZoneRow & { cropName: string | null }

export const IrrigationActivityByZone = ({
  propertyIds,
  dateFrom,
  dateTo,
  zoneCrops,
  refreshToken,
  onLoaded,
}: {
  propertyIds: number[]
  dateFrom: moment.Moment
  dateTo: moment.Moment
  zoneCrops: TZoneCrops
  refreshToken?: number
  // Reports the widget's last successful fetch time up to the shell's "Last Updated".
  onLoaded?: (date: Date) => void
}) => {
  // Rain + ETc are shown when the user has the relevant permission on any selected property.
  const showRain = useMemo(
    () => propertyIds.some((id) => checkAuthorization({ permission: 'VIEW_WEATHER_DETAILS', entity: id })),
    [propertyIds],
  )

  const showEtc = useMemo(
    () =>
      propertyIds.some((id) =>
        checkAuthorization({ permission: 'VIEW_EVAPOTRANSPIRATION_DETAILS', entity: id }),
      ),
    [propertyIds],
  )

  const { sections, loading, error, lastFetchedAt } = useIrrigationZoneActivity({
    propertyIds,
    dateFrom,
    dateTo,
    includeRain: showRain,
    includeEtc: showEtc,
    refreshToken,
  })

  useEffect(() => {
    if (lastFetchedAt) onLoaded?.(lastFetchedAt)
  }, [lastFetchedAt, onLoaded])

  const showCrops = sections.some((section) => zoneCrops.multiCropPropertyIds.has(section.propertyId))

  const sectionsWithCrops = useMemo(
    () => withCropNames(sections, (row) => [row.zoneId], zoneCrops, showCrops),
    [sections, zoneCrops, showCrops],
  )

  const columns = useMemo<TGroupedTableColumn<TZoneRowWithCrop>[]>(() => {
    const base: TGroupedTableColumn<TZoneRowWithCrop>[] = [
      ...(showCrops ? [getCropColumn<TZoneRowWithCrop>()] : []),
      {
        key: 'zone',
        header: translate.phrases.banyanApp('Zone'),
        value: (row) => row.zoneName,
        // Group column: blanks on repeat so each zone's name shows once above its emitters.
        collapseRepeats: true,
      },
      {
        key: 'emitter',
        header: translate.phrases.banyanApp('Emitter'),
        value: (row) => mapApiEmitterTypeToLabel(row.emitterType),
      },
      {
        key: 'applicationRate',
        header: translate.phrases.banyanApp('Application Rate'),
        value: (row) => formatApplicationRate(row.applicationRate),
        render: (row) => formatApplicationRate(row.applicationRate) || '-',
      },
      {
        key: 'volume',
        header: translate.phrases.banyanApp('Total Volume'),
        value: (row) => formatVolume(row.volumeMm),
        render: (row) => formatVolume(row.volumeMm) || '-',
      },
      {
        key: 'duration',
        header: translate.phrases.banyanApp('On Duration'),
        value: (row) => formatDuration(row.durationMs),
        render: (row) => formatDuration(row.durationMs) || '-',
      },
    ]

    if (showRain) {
      base.push({
        key: 'rain',
        header: translate.phrases.banyanApp('Rain'),
        value: (row) => formatRain(row.rainSum),
        render: (row) => formatRain(row.rainSum) || '-',
      })
    }

    if (showEtc) {
      base.push({
        key: 'etc',
        header: translate.phrases.banyanApp('ETc'),
        value: (row) => formatEtc(row.etcSum),
        render: (row) => formatEtc(row.etcSum) || '-',
      })
    }

    return base
  }, [showRain, showEtc, showCrops])

  return (
    <DashboardGroupedTable
      title={translate.phrases.banyanApp('Irrigation Activity by Zone')}
      sections={sectionsWithCrops}
      columns={columns}
      loading={loading}
      error={error}
      showPropertyCount
      downloadFileName={translate.phrases.banyanApp('Irrigation Activity by Zone')}
    />
  )
}
